import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { gatherClasses, debounce, isFunction } from "../../lib/utils";
import Tooltip from "../tooltip/tooltip";
import Badge from "../badge/badge";

class TextTruncate extends PureComponent {
  constructor(props) {
    super(props);

    this.element = React.createRef();
    this.original = props.text;
    this.watch = true;
    this.lineHeight = 0;
    this.start = 0;
    this.middle = 0;
    this.end = 0;
    this.state = {
      expanded: true,
      noClamp: false,
      text: props.text.substring(0, 20)
    };

    this.action = this.action.bind(this);
    this.clickHandler = this.clickHandler.bind(this);
    this.debounced = debounce(this.action, 300);
  }

  componentDidMount() {
    const { text } = this.props;
    if (text) {
      this.lineHeight = this.element.current.clientHeight + 1;
      this.clampLines();

      if (this.watch) {
        window.addEventListener("resize", this.debounced);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.debounced);
  }

  componentDidUpdate(prevProps) {
    const { text } = this.props;
    if (prevProps.text !== text) {
      this.original = text;
      this.clampLines();
    }
  }

  action() {
    const { expanded } = this.state;
    if (this.watch) {
      this.setState({
        noClamp: false
      });
      this.clampLines();
      this.setState({ expanded: !expanded });
    }
  }

  clampLines() {
    const { lines } = this.props;
    if (!this.element.current) return;

    this.setState({
      text: ""
    });

    const maxHeight = this.lineHeight * lines + 1;

    this.start = 0;
    this.middle = 0;
    this.end = this.original.length;

    while (this.start <= this.end) {
      this.middle = Math.floor((this.start + this.end) / 2);
      this.element.current.innerText = this.original.slice(0, this.middle);
      if (this.middle === this.original.length) {
        this.setState({
          text: this.original,
          noClamp: true
        });
        return;
      }

      this.moveMarkers(maxHeight);
    }

    this.element.current.innerText =
      this.original.slice(0, this.middle - 4) + this.getEllipsis();

    this.setState({
      text: this.original.slice(0, this.middle - 4) + this.getEllipsis()
    });
  }

  moveMarkers(maxHeight) {
    if (this.element.current.clientHeight <= maxHeight) {
      this.start = this.middle + 1;
    } else {
      this.end = this.middle - 1;
    }
  }

  getEllipsis() {
    const { noClamp } = this.state;
    return this.watch && !noClamp ? "..." : "";
  }

  getButton() {
    const { noClamp } = this.state;
    const { showMoreButton } = this.props;

    if (noClamp || !showMoreButton) return;
    const buttonText = this.watch ? "More" : "Less";

    // eslint-disable-next-line consistent-return
    return (
      <Badge
        className="oui-ml-0"
        label={buttonText}
        onClick={this.clickHandler}
      />
    );
  }

  clickHandler(_, e) {
    const { expanded } = this.state;
    e.preventDefault();

    this.watch = !this.watch;

    if (this.watch) {
      this.clampLines();
    } else {
      this.setState({
        text: this.original
      });
    }

    this.setState({ expanded: !expanded });
  }

  render() {
    let tooltipContent = this.original;
    const {
      text,
      tooltipConfig: { placement = "auto", customRenderer, maxWidth, textAlign }
    } = this.props;

    const { text: textToShow, noClamp } = this.state;

    if (isFunction(customRenderer)) {
      tooltipContent = () => customRenderer(this.original);
    }

    if (!text) {
      return null;
    }

    return (
      <div className={gatherClasses(this.props)}>
        {!noClamp && this.watch ? (
          <Tooltip
            text={tooltipContent}
            placement={placement}
            maxWidth={maxWidth}
            textAlign={textAlign}
          >
            <div className="oui-mb-1" ref={this.element}>
              {textToShow}
            </div>
          </Tooltip>
        ) : (
          <div className="oui-mb-1" ref={this.element}>
            {textToShow}
          </div>
        )}
        {this.getButton()}
      </div>
    );
  }
}

TextTruncate.propTypes = {
  text: PropTypes.string.isRequired,
  lines: PropTypes.number,
  showMoreButton: PropTypes.bool,
  tooltipConfig: PropTypes.shape({
    placement: PropTypes.oneOf(["auto", "top", "right", "bottom", "left"]),
    textAlign: PropTypes.oneOf(["left", "center", "right"]),
    maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customRenderer: PropTypes.func
  })
};

TextTruncate.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  text: "",
  lines: 3,
  showMoreButton: true,
  tooltipConfig: {}
};

export default TextTruncate;
